import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Calendar, ChevronDown, Send, Loader2 } from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import { SERVICES, SERVICE_AREAS, PHONE, PHONE_HREF, EMAIL, EMAIL_HREF } from "../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", phone: "", service: "", preferred_date: "", notes: "" };

const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-[#0A192F] placeholder:text-slate-400 outline-none transition-all duration-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10";

export default function Contact() {
    const [form, setForm] = useState(initialForm);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        document.title = "Contact & Book | Chirolane & Wellness, LLC — DFW Chiropractor";
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/appointments`, form);
            toast.success("Appointment request sent!", {
                description: "Dr. Lane's office will reach out shortly to confirm your visit.",
            });
            setForm(initialForm);
        } catch (err) {
            toast.error("Something went wrong", {
                description: "Please try again, or call us at 214-814-3929.",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <main>
            <section className="hero-grain relative bg-gradient-to-b from-blue-50/70 via-slate-50 to-white pt-32 sm:pt-40 pb-16 sm:pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                    <p className="text-xs uppercase tracking-[0.3em] font-semibold text-blue-800 mb-6 flex items-center gap-3">
                        <span className="h-px w-10 bg-blue-700 inline-block" /> Contact &amp; Book
                    </p>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.08] text-[#0A192F] max-w-3xl">
                        <MaskedLine delay={0.2}>Let's get you</MaskedLine>
                        <MaskedLine delay={0.35} className="italic text-blue-700">moving better</MaskedLine>
                    </h1>
                </div>
            </section>

            <section className="py-16 sm:py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                    <Reveal>
                        <div data-testid="jane-booking-panel" className="relative overflow-hidden rounded-3xl bg-[#0A192F] text-white p-8 sm:p-14">
                            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-blue-700/30 blur-3xl" aria-hidden="true" />
                            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                                <div className="lg:col-span-8">
                                    <p className="text-xs uppercase tracking-[0.3em] font-semibold text-sky-400 mb-4">Fastest Option</p>
                                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
                                        Pick your time <span className="italic text-sky-300">instantly</span>
                                    </h2>
                                    <p className="mt-5 text-base font-light leading-relaxed text-slate-300 max-w-xl">
                                        See Dr. Lane's live availability and lock in your visit through our secure online booking portal, powered by Jane App. Weekend appointments available.
                                    </p>
                                </div>
                                <div className="lg:col-span-4 lg:text-right">
                                    <a
                                        data-testid="jane-schedule-button"
                                        href="https://chirolanewellness.janeapp.com"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center justify-center gap-2 rounded-full bg-sky-400 text-[#0A192F] text-sm font-bold px-9 py-4 w-full lg:w-auto transition-all duration-300 hover:bg-white hover:-translate-y-0.5 hover:shadow-xl"
                                    >
                                        <Calendar className="h-4 w-4" />
                                        Schedule on Jane App
                                    </a>
                                    <p className="mt-3 text-xs text-slate-400">Opens our secure booking portal</p>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="pb-16 sm:pb-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                    <Reveal>
                        <div className="flex items-center gap-5 mb-12">
                            <span className="h-px flex-1 bg-slate-200" />
                            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-slate-400 text-center">Or send a request — we'll confirm personally</p>
                            <span className="h-px flex-1 bg-slate-200" />
                        </div>
                    </Reveal>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* FORM */}
                    <Reveal className="lg:col-span-7">
                        <form
                            data-testid="appointment-form"
                            onSubmit={submit}
                            className="rounded-3xl border border-slate-200 bg-white shadow-xl shadow-blue-900/5 p-7 sm:p-10"
                        >
                            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#0A192F]">Request an Appointment</h2>
                            <p className="mt-2 text-sm text-slate-500">Tell us a little about you — we'll confirm your visit personally.</p>
                            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="form-name" className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Full Name</label>
                                    <input id="form-name" data-testid="form-input-name" required value={form.name} onChange={set("name")} placeholder="Jordan Smith" className={inputCls} />
                                </div>
                                <div>
                                    <label htmlFor="form-phone" className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Phone</label>
                                    <input id="form-phone" data-testid="form-input-phone" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(214) 555-0100" className={inputCls} />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="form-email" className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Email</label>
                                    <input id="form-email" data-testid="form-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
                                </div>
                                <div className="relative">
                                    <label htmlFor="form-service" className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Service</label>
                                    <select
                                        id="form-service"
                                        data-testid="form-select-service"
                                        required
                                        value={form.service}
                                        onChange={set("service")}
                                        className={`${inputCls} appearance-none pr-10 ${form.service ? "" : "text-slate-400"}`}
                                    >
                                        <option value="" disabled>Select a service</option>
                                        {SERVICES.map((s) => (
                                            <option key={s.id} value={s.title}>{s.title}</option>
                                        ))}
                                        <option value="Not sure yet">Not sure yet — help me decide</option>
                                    </select>
                                    <ChevronDown className="h-4 w-4 text-slate-400 absolute right-4 bottom-4 pointer-events-none" />
                                </div>
                                <div>
                                    <label htmlFor="form-date" className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Preferred Date</label>
                                    <input
                                        id="form-date"
                                        data-testid="form-input-date"
                                        type="date"
                                        value={form.preferred_date}
                                        onChange={set("preferred_date")}
                                        min={new Date().toISOString().split("T")[0]}
                                        className={inputCls}
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="form-notes" className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Health Goals / Notes</label>
                                    <textarea
                                        id="form-notes"
                                        data-testid="form-input-notes"
                                        rows={4}
                                        value={form.notes}
                                        onChange={set("notes")}
                                        placeholder="Tell us what's going on — pain, goals, sport, anything that helps."
                                        className={`${inputCls} resize-none`}
                                    />
                                </div>
                            </div>
                            <button
                                data-testid="form-submit-button"
                                type="submit"
                                disabled={sending}
                                className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-blue-700 text-white text-sm font-semibold px-8 py-4 transition-all duration-300 hover:bg-[#0A192F] hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.99]"
                            >
                                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                {sending ? "Sending..." : "Send Appointment Request"}
                            </button>
                        </form>
                    </Reveal>

                    {/* INFO */}
                    <div className="lg:col-span-5 space-y-6">
                        <Reveal delay={0.1}>
                            <div className="rounded-3xl bg-[#0A192F] text-white p-8 sm:p-10 space-y-6">
                                <h2 className="font-display text-2xl sm:text-3xl font-semibold">Prefer to reach out directly?</h2>
                                <a data-testid="contact-phone-link" href={PHONE_HREF} className="flex items-center gap-4 group">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-sky-300 group-hover:bg-sky-400 group-hover:text-[#0A192F] transition-colors"><Phone className="h-4 w-4" /></span>
                                    <span>
                                        <span className="block text-xs uppercase tracking-[0.2em] text-slate-400">Call or Text</span>
                                        <span className="block font-semibold">{PHONE}</span>
                                    </span>
                                </a>
                                <a data-testid="contact-email-link" href={EMAIL_HREF} className="flex items-center gap-4 group">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-sky-300 group-hover:bg-sky-400 group-hover:text-[#0A192F] transition-colors"><Mail className="h-4 w-4" /></span>
                                    <span>
                                        <span className="block text-xs uppercase tracking-[0.2em] text-slate-400">Email</span>
                                        <span className="block font-semibold break-all">{EMAIL}</span>
                                    </span>
                                </a>
                                <div className="flex items-center gap-4">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-sky-300"><Clock className="h-4 w-4" /></span>
                                    <span>
                                        <span className="block text-xs uppercase tracking-[0.2em] text-slate-400">Hours</span>
                                        <span className="block font-semibold">Weekends only · Sat–Sun, by appointment</span>
                                    </span>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={0.18}>
                            <div className="rounded-3xl border border-slate-200 bg-blue-50/60 p-8 sm:p-10">
                                <h3 className="flex items-center gap-3 font-display text-2xl font-semibold text-[#0A192F]">
                                    <MapPin className="h-5 w-5 text-blue-700" /> DFW Service Area
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                                    Proudly serving the Dallas–Fort Worth Metroplex. Mobile visit options may be available in your area — ask when you book.
                                </p>
                                <div data-testid="service-area-list" className="mt-6 flex flex-wrap gap-2.5">
                                    {SERVICE_AREAS.map((area) => (
                                        <span key={area} className="rounded-full border border-blue-200 bg-white px-4 py-2 text-xs font-medium text-slate-700">
                                            {area}
                                        </span>
                                    ))}
                                </div>
                                <p className="mt-6 text-xs text-slate-400">…and surrounding DFW communities.</p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.25}>
                            <div className="rounded-3xl border border-slate-200 bg-white p-8 flex items-start gap-4">
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-700"><Calendar className="h-4 w-4" /></span>
                                <p className="text-sm leading-relaxed text-slate-600">
                                    <span className="font-semibold text-[#0A192F]">New patients welcome.</span> Most requests are confirmed within one business day — often sooner.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
